import React, {FC, useEffect, useMemo, useState} from 'react'
import {CrewDeduction, DeductionStatus} from '../core/_requests'

type Props = {
  visible: boolean
  claim: CrewDeduction | null
  onClose: () => void
  onSave: (officeRemarks: string) => void
  onCloseSatisfactory: (officeRemarks: string) => void
  isLoading?: boolean
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 1050,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

const getStatusDisplay = (status: DeductionStatus) => {
  if (status === 'DRAFT') return {label: 'Draft', badgeClass: 'badge-light-secondary'}
  if (status === 'SUBMITTED') return {label: 'Ready for Review', badgeClass: 'badge-light-warning'}
  if (status === 'REVIEWED') return {label: 'Reviewed', badgeClass: 'badge-light-success'}
  if (status === 'REJECTED') return {label: 'Rejected', badgeClass: 'badge-light-danger'}
  return {label: String(status), badgeClass: 'badge-light'}
}

const formatClaimMonth = (dateStr: string | null | undefined) => {
  if (!dateStr) return '-'
  try {
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-IN', {
      month: 'long',
      year: 'numeric',
    })
  } catch {
    return dateStr
  }
}

export const CrewDeductionReviewModal: FC<Props> = ({
  visible,
  claim,
  onClose,
  onSave,
  onCloseSatisfactory,
  isLoading = false,
}) => {
  const [officeRemarks, setOfficeRemarks] = useState('')
  const [remarksError, setRemarksError] = useState('')

  useEffect(() => {
    if (!visible || !claim) return
    setOfficeRemarks(claim.officeRemarks || '')
    setRemarksError('')
  }, [visible, claim])

  const statusMeta = useMemo(() => {
    if (!claim) return {label: '-', badgeClass: 'badge-light'}
    return getStatusDisplay(claim.status)
  }, [claim])

  const validateRemarks = () => {
    if (!officeRemarks || !officeRemarks.trim()) {
      setRemarksError('Office remarks are required.')
      return false
    }
    setRemarksError('')
    return true
  }

  if (!visible || !claim) return null

  return (
    <div style={WRAP_STYLE} tabIndex={-1}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{
          width: '70vw',
          maxWidth: '900px',
          maxHeight: '80vh',
        }}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-5 py-4 flex-shrink-0'>
          <div>
            <h5 className='modal-title text-dark m-0'>Review Crew Deduction</h5>
            <div className='text-muted small mt-1'>
              {claim.crewName || '-'} | {claim.rankName || '-'} | {formatClaimMonth(claim.claimMonth)}
            </div>
          </div>
          <div className='d-flex align-items-center gap-3'>
            <span className={`badge ${statusMeta.badgeClass}`}>{statusMeta.label}</span>
            <button type='button' className='btn-close m-0' onClick={onClose} disabled={isLoading} />
          </div>
        </div>

        <div className='flex-grow-1 overflow-auto px-5 py-4'>
          <div className='mb-4'>
            <label className={LABEL}>
              Office Remarks <span style={{color: '#dc3545'}}>*</span>
            </label>
            <textarea
              className='form-control'
              rows={4}
              value={officeRemarks}
              onChange={(e) => {
                setOfficeRemarks(e.target.value)
                if (remarksError) setRemarksError('')
              }}
              placeholder='Add office review remarks'
              disabled={isLoading}
            />
            {remarksError && <div className='text-danger small mt-2'>{remarksError}</div>}
          </div>
        </div>

        <div className='border-top d-flex justify-content-end gap-2 px-5 py-4 flex-shrink-0'>
          <button type='button' className='btn btn-light btn-sm' onClick={onClose} disabled={isLoading}>
            Cancel
          </button>
          <button
            type='button'
            className='btn btn-light-primary btn-sm'
            disabled={isLoading}
            onClick={() => {
              if (!validateRemarks()) return
              onSave(officeRemarks)
            }}
          >
            Save Review Only
          </button>
          <button
            type='button'
            className='btn btn-success btn-sm'
            disabled={isLoading}
            onClick={() => {
              if (!validateRemarks()) return
              onCloseSatisfactory(officeRemarks)
            }}
          >
            Closed Satisfactory
          </button>
        </div>
      </div>
    </div>
  )
}

export default CrewDeductionReviewModal

